/**
 * Friend Inbox effects — isolated side-effect handlers.
 *
 * Elm-ish architecture: every API call lives here. The effect runner
 * (useFriendInboxProgram) calls these functions and feeds the result
 * back into the model via dispatch().
 *
 * Security posture:
 * - No credentials or tokens are logged.
 * - Error messages are safe for surface (the server is the authority
 *   for any actual authorization decision; UI just conveys what
 *   the server told it).
 * - The Friend-side endpoints are server-bound; the API client never
 *   trusts the UI to make an authorization decision.
 */
import {
  acceptIntroRequest,
  getRecipientIntroRequest,
  listRecipientInbox,
  rejectIntroRequest,
  type IntroDetailResponse,
  type IntroInboxResponse,
} from '@/api/introRequests';
import type { FriendInboxMsg } from './messages';

/**
 * Effect produced by update. The effect runner consumes these; the
 * update function itself does not perform any side effect.
 */
export type FriendInboxEffect =
  | { readonly type: 'ListInbox'; readonly token: number }
  | { readonly type: 'GetIntro'; readonly id: string; readonly token: number }
  | { readonly type: 'AcceptIntro'; readonly id: string; readonly token: number }
  | { readonly type: 'RejectIntro'; readonly id: string; readonly token: number };

/**
 * Effect runner context — wired by the program harness. Provides
 * dispatch() for handlers to send back messages.
 */
export interface EffectContext {
  dispatch: (msg: FriendInboxMsg) => void;
}

/**
 * Run a single effect to completion, dispatching the resulting
 * message back to the model.
 */
export async function runEffect(
  effect: FriendInboxEffect,
  context: EffectContext,
): Promise<void> {
  switch (effect.type) {
    case 'ListInbox':
      await runListInbox(effect.token, context);
      return;

    case 'GetIntro':
      await runGetIntro(effect.id, effect.token, context);
      return;

    case 'AcceptIntro':
      await runAcceptIntro(effect.id, effect.token, context);
      return;

    case 'RejectIntro':
      await runRejectIntro(effect.id, effect.token, context);
      return;

    default: {
      const _exhaustive: never = effect;
      void _exhaustive;
      return;
    }
  }
}

async function runListInbox(token: number, context: EffectContext): Promise<void> {
  try {
    const response: IntroInboxResponse = await listRecipientInbox(50, 0);
    context.dispatch({ type: 'LoadSucceeded', response, token });
  } catch (err) {
    const error = err instanceof Error ? err.message : 'unknown';
    context.dispatch({ type: 'LoadFailed', error, token });
  }
}

async function runGetIntro(id: string, _token: number, context: EffectContext): Promise<void> {
  try {
    const detail: IntroDetailResponse | null = await getRecipientIntroRequest(id);
    if (!detail) {
      context.dispatch({
        type: 'ItemSelectedFailed',
        error: 'Intro request not found.',
      });
      return;
    }
    context.dispatch({ type: 'ItemSelectedSucceeded', detail });
  } catch (err) {
    const error = err instanceof Error ? err.message : 'unknown';
    context.dispatch({ type: 'ItemSelectedFailed', error });
  }
}

async function runAcceptIntro(id: string, _token: number, context: EffectContext): Promise<void> {
  try {
    const detail: IntroDetailResponse = await acceptIntroRequest(id);
    context.dispatch({ type: 'TransitionSucceeded', detail });
  } catch (err) {
    const error = err instanceof Error ? err.message : 'unknown';
    context.dispatch({ type: 'TransitionFailed', error });
  }
}

async function runRejectIntro(id: string, _token: number, context: EffectContext): Promise<void> {
  try {
    const detail: IntroDetailResponse = await rejectIntroRequest(id);
    context.dispatch({ type: 'TransitionSucceeded', detail });
  } catch (err) {
    const error = err instanceof Error ? err.message : 'unknown';
    context.dispatch({ type: 'TransitionFailed', error });
  }
}
